import * as React from "react"
import { ChevronDown, ChevronUp, Filter, Search, X } from "lucide-react"

import { NETWORKS, type NetworkSlug } from "@/lib/networks"
import { TAP_TARGET } from "@/lib/ui-tokens"
import { cn } from "@/lib/utils"
import { Button } from "@/shadcn/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu"
import { Input } from "@/shadcn/components/ui/input"
import { NetworkBadge } from "@/shadcn/components/ui/networks"
import { Separator } from "@/shadcn/components/ui/separator"
import { SourceChip } from "./marks"
import {
  ConnectNewTile,
  OutputTile,
  type OutputAccount,
} from "./OutputTile"
import {
  type ConnectedSource,
  type OutputSort,
  type PostingGroup,
} from "./types"

/**
 * Step 2 · choose destinations.
 *
 * Narrowing (search, network filter, sort) is owned here, since
 * it only changes WHICH accounts are displayed. Selection, the account list and
 * the counts stay with the caller.
 *
 * An account whose service has no brand mark carries `network: null` and is
 * simply never matched by the network filter — prod has such services
 * (StatusNet), so every registry lookup below is guarded rather than assuming a
 * label exists.
 */

/** Registry label, or "" for an account with no brand mark. */
function networkLabel(network: NetworkSlug | null) {
  return network ? NETWORKS[network].label : ""
}

function DestinationsEmpty({
  networks,
  more,
  moreOpen: moreOpenInitial,
  onConnectNetwork,
}: {
  networks: NetworkSlug[]
  more: NetworkSlug[]
  /** Initial disclosure state — the harness pins it to draw the open frame. */
  moreOpen: boolean
  onConnectNetwork?: (network: NetworkSlug) => void
}) {
  const [moreOpen, setMoreOpen] = React.useState(moreOpenInitial)

  /**
   * A badge is the only route to connecting a first destination, so when the
   * flow supplies a handler each one becomes a real button with the network's
   * name on it — the badge itself is decorative once it sits inside a labelled
   * control.
   */
  const badge = (network: NetworkSlug, size: string, glyph: string) => {
    const mark = (
      <NetworkBadge network={network} size={size} glyph={glyph} />
    )
    if (!onConnectNetwork) return mark
    return (
      <button
        type="button"
        aria-label={`Connect ${NETWORKS[network].label}`}
        onClick={() => onConnectNetwork(network)}
        className="tw:rounded-full tw:outline-none tw:transition-opacity tw:hover:opacity-80 tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50"
      >
        {mark}
      </button>
    )
  }

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:items-center tw:gap-6 tw:py-6">
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-1.5 tw:text-center">
        <p className="tw:text-base tw:font-semibold tw:text-foreground">
          Connect your first destination
        </p>
        <p className="tw:text-sm tw:text-muted-foreground">
          Pick a network to get started — you can add more anytime.
        </p>
      </div>

      <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-x-5 tw:gap-y-4">
        {networks.map((network) => (
          <React.Fragment key={network}>
            {badge(
              network,
              "tw:size-14 tw:sm:size-[75px]",
              "tw:text-[34px] tw:sm:text-[45px]"
            )}
          </React.Fragment>
        ))}
      </div>

      <button
        type="button"
        className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:px-2.5 tw:py-3 tw:text-sm tw:font-medium tw:text-foreground tw:transition-colors tw:outline-none tw:hover:bg-muted tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:md:py-2"
        aria-expanded={moreOpen}
        onClick={() => setMoreOpen((open) => !open)}
      >
        More destinations
        {moreOpen ? (
          <ChevronUp className="tw:size-4" />
        ) : (
          <ChevronDown className="tw:size-4" />
        )}
      </button>

      {moreOpen ? (
        /* grid-cols-5 at mobile was 5x56 + 4x20 gap = 384px against 303px
           available, pushing the page sideways. 3 -> 5 -> 7 instead. */
        <div className="tw:grid tw:grid-cols-3 tw:justify-items-center tw:gap-x-5 tw:gap-y-4 tw:sm:grid-cols-5 tw:md:grid-cols-7">
          {more.map((network) => (
            <React.Fragment key={network}>
              {badge(network, "tw:size-14", "tw:text-[34px]")}
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function DestinationsStep({
  base,
  source,
  accounts,
  showToolbar,
  filterOpen,
  filterCounts,
  postingGroups,
  emptyNetworks,
  moreDestinations,
  moreDestinationsOpen,
  onSelectAccount,
  onSelectGroup,
  onConnectNew,
  onConnectNetwork,
}: {
  base: string
  source: ConnectedSource
  accounts: OutputAccount[]
  showToolbar: boolean
  filterOpen: boolean
  filterCounts: Array<{ network: NetworkSlug; label: string; count: number }>
  postingGroups: PostingGroup[]
  emptyNetworks: NetworkSlug[]
  moreDestinations: NetworkSlug[]
  moreDestinationsOpen: boolean
  /** Toggles an account. Omit to render the grid inert. */
  onSelectAccount?: (account: OutputAccount) => void
  /**
   * A saved posting group was picked. It is a bulk-select shortcut, NOT a filter:
   * the caller adds whichever of the group's members it holds to the selection
   * and the grid keeps showing everything. Omit to render the submenu inert.
   */
  onSelectGroup?: (group: PostingGroup) => void
  onConnectNew?: () => void
  /** Empty state only — picking a network to connect first. */
  onConnectNetwork?: (network: NetworkSlug) => void
}) {
  /*
   * The first state in this file, and deliberately narrow: search text and the
   * network filter only change WHICH accounts are displayed. Selection, the
   * account list and the counts all stay owned by the caller, so this can still
   * be wired around rather than inside.
   *
   * Declared above the empty-account early return — hooks have to run
   * unconditionally, so the return moved below them.
   */
  const [query, setQuery] = React.useState("")
  const [searchOpen, setSearchOpen] = React.useState(false)
  const [networks, setNetworks] = React.useState<NetworkSlug[]>([])
  // null, not "a-z": sorting is opt-in, so the grid opens in the order the
  // caller supplied and nothing in the sort menu reads as pre-selected.
  const [sort, setSort] = React.useState<OutputSort | null>(null)

  const trimmed = query.trim().toLowerCase()

  const visible = React.useMemo(() => {
    const matched = accounts.filter((account) => {
      if (networks.length) {
        if (!account.network || !networks.includes(account.network)) return false
      }
      if (!trimmed) return true
      // Network label as well as account name: typing "facebook" should find
      // the Facebook accounts, which is what people reach for first.
      return (
        account.name.toLowerCase().includes(trimmed) ||
        networkLabel(account.network).toLowerCase().includes(trimmed)
      )
    })

    if (!sort) return matched

    // Copy before sorting — `accounts` is a prop, and Array#sort mutates.
    const byName = (a: OutputAccount, b: OutputAccount) =>
      // localeCompare with numeric so "demo2" follows "demo10" sensibly, and
      // case-insensitively so "@dlvritdemo" doesn't sort away from "blog".
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })

    return [...matched].sort((a, b) => {
      if (sort === "a-z") return byName(a, b)
      if (sort === "z-a") return byName(b, a)
      // "type" = network, the thing the tiles are badged by. Ties fall back to
      // name so the order inside a network is stable rather than incidental.
      return (
        networkLabel(a.network).localeCompare(networkLabel(b.network)) ||
        byName(a, b)
      )
    })
  }, [accounts, networks, trimmed, sort])

  if (!accounts.length) {
    return (
      <DestinationsEmpty
        networks={emptyNetworks}
        more={moreDestinations}
        moreOpen={moreDestinationsOpen}
        onConnectNetwork={onConnectNetwork}
      />
    )
  }

  // Counted across every account, not the filtered view: this is how many
  // destinations the automation will post to, which narrowing must not change.
  const selected = accounts.filter((a) => a.selected).length
  const narrowed = Boolean(trimmed) || networks.length > 0

  const closeSearch = () => {
    setSearchOpen(false)
    setQuery("")
  }

  /*
   * Single-select: picking a network replaces the filter rather than adding to
   * it, and the menu closes on the pick. `networks` stays an array because
   * everything downstream reads it as one — the state is "zero or one network",
   * not "a set".
   */
  const toggleNetwork = (network: NetworkSlug, on: boolean) =>
    setNetworks(on ? [network] : [])

  // The network's own name rather than "1 network", with filterCounts owning
  // the wording (e.g. "Instagram Professional").
  const filterLabel =
    networks.length === 0
      ? "All Outputs"
      : (filterCounts.find((c) => c.network === networks[0])?.label ??
        networkLabel(networks[0]))

  return (
    <div className="tw:flex tw:w-full tw:flex-col tw:gap-4">
      <SourceChip source={source} base={base} />
      {/* The empty state names the task ("Connect your first destination") and
          this path didn't, so the screen gained and lost its framing depending
          on whether you had any accounts. Same treatment ReviewStep's "Your
          automation" and the empty state's own heading use.

          "Accounts" rather than "profiles": it is the word the rest of the flow
          uses — the OutputAccount type, the review step's "N accounts across M
          networks" — and it stays true for the Pages, blogs, Google Business
          locations and Slack channels in this list.

          No supporting line under it. The shell already prints "Choose where
          we'll post your content." above, and the count below is live.

          Below the source chip, not above it: the chip answers "from
          what", which the flow has just established, and this heading
          opens the question this step actually asks. */}
      <p className="tw:text-base tw:font-semibold tw:text-foreground">
        Select accounts to post to
      </p>

      {showToolbar ? (
        <div className="tw:flex tw:flex-col tw:gap-3">
          <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
            {/* The search field takes the scope trigger's place rather than
                sitting beside it. Three controls plus a field don't fit the
                3-column mobile width, and while you're searching the scope
                label is the least useful of them — the filter button keeps
                showing that a filter is active. */}
            {searchOpen ? (
              <div className="tw:relative tw:min-w-0 tw:flex-1">
                <Search className="tw:pointer-events-none tw:absolute tw:top-1/2 tw:left-2.5 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") closeSearch()
                  }}
                  placeholder="Search outputs"
                  aria-label="Search outputs"
                  className={cn(TAP_TARGET, "tw:pl-8 tw:md:h-8")}
                />
              </div>
            ) : (
              /* defaultOpen keeps the `filterOpen` prop working as the
                 review affordance it was: a static frame can still be captured
                 with the menu down, now without a parallel non-portaling copy
                 of the panel to keep in sync.

                 modal={false} because a modal Radix menu puts
                 pointer-events:none on the body and locks the scroll position,
                 so with the filter down the grid underneath couldn't be
                 scrolled. Nothing here needs a focus trap — it is a toolbar
                 popover, and outside clicks still dismiss it. */
              <DropdownMenu defaultOpen={filterOpen} modal={false}>
                {/* py-3 -> 44px at mobile. At py-1 this was 28px, the smallest
                    target in the flow. Same reasoning as TAP_TARGET, inline
                    because this control is padding-sized rather than
                    height-sized. */}
                <DropdownMenuTrigger className="tw:inline-flex tw:items-center tw:gap-1.5 tw:rounded-md tw:py-3 tw:text-sm tw:font-medium tw:text-foreground tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-ring/50 tw:md:py-1">
                  {filterLabel}
                  <ChevronDown className="tw:size-4" />
                </DropdownMenuTrigger>
                {/* Portal patch: portalled content mounts outside `.tw-root`, so
                    without the class it renders against the legacy stylesheet's
                    resets. The primitive used to carry this on a wrapper; it now
                    lives at the call sites, and these two were missed. */}
                <DropdownMenuContent
                  align="start"
                  className="tw-root tw:w-[228px]"
                >
                  {/* Checkbox items for a single-select list, deliberately: the
                      Figma rows are drawn with the checkbox's tick on the right,
                      and a radio item draws a dot. Only one can be on at a time
                      — see toggleNetwork. */}
                  <DropdownMenuCheckboxItem
                    checked={networks.length === 0}
                    onCheckedChange={() => setNetworks([])}
                  >
                    <span className="tw:flex-1">All Outputs</span>
                  </DropdownMenuCheckboxItem>
                  <DropdownMenuSeparator />
                  {filterCounts.map((entry) => (
                    <DropdownMenuCheckboxItem
                      key={`${entry.network}-${entry.label}`}
                      checked={networks.includes(entry.network)}
                      onCheckedChange={(on) => toggleNetwork(entry.network, on)}
                    >
                      {/* The same brand-disc badge the tiles wear, so a filter
                          row looks like the thing it narrows. Bare marks were
                          tried here and vary wildly in optical weight at 18px —
                          Snapchat is a white ghost, Threads solid black — while
                          the disc gives every network one footprint and puts a
                          brand colour behind the pale glyphs. */}
                      <NetworkBadge
                        network={entry.network}
                        size={18}
                        className="tw:shrink-0"
                        decorative
                      />
                      <span className="tw:min-w-0 tw:flex-1 tw:truncate">
                        {entry.label}
                      </span>
                      {/* Sits inside the item's pr-8, so it clears the check
                          indicator the radix-nova style puts on the right. */}
                      <span className="tw:shrink-0 tw:text-muted-foreground">
                        {entry.count}
                      </span>
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
              {/* size-11, not TAP_TARGET: an icon button needs both dimensions,
                  and `size="icon"` is a plain `size-8` in the cva, so
                  tailwind-merge resolves it without an important. */}
              <Button
                variant="ghost"
                size="icon"
                className="tw:size-11 tw:md:size-8"
                aria-label={searchOpen ? "Close search" : "Search outputs"}
                aria-expanded={searchOpen}
                onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
              >
                {searchOpen ? <X /> : <Search />}
              </Button>
              {/* The sort menu from Figma 28630:2467 — Sort A–Z / Z–A / By Type,
                  then Choose Posting Group. Anchored `end` to match the design,
                  where it hangs off these icons rather than the scope trigger.

                  Sort is a radio group because the orders are exclusive, and
                  every menu in this toolbar closes on select. */}
              {/* modal={false} for the same reason as the filter beside it: a
                  modal menu freezes the page behind it. */}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="tw:size-11 tw:md:size-8"
                    aria-label="Sort and group outputs"
                  >
                    <Filter />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="tw-root tw:w-52">
                  <DropdownMenuRadioGroup
                    value={sort ?? ""}
                    onValueChange={(value) => setSort(value as OutputSort)}
                  >
                    <DropdownMenuRadioItem value="a-z">Sort A – Z</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="z-a">Sort Z – A</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="type">Sort By Type</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>

                  {/* Dropped entirely when the account has no groups: the
                      submenu would open onto nothing. Posting groups are an
                      opt-in feature, so an empty list is the common case rather
                      than an edge one. */}
                  {postingGroups.length ? (
                    <>
                      <DropdownMenuSeparator />

                      {/* A submenu, not a fourth flat item: Figma draws four rows
                          at rest, and this is the only one that opens onto a
                          list.

                          Plain items, not a radio group: picking a group ADDS its
                          members to the selection and is finished. There is no
                          "current group" to show a dot against, and no "All
                          groups" row, because there is nothing to undo — the
                          tiles it selected are the state, and they untoggle
                          individually like any other. */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          Choose Posting Group
                        </DropdownMenuSubTrigger>
                        {/* Belt and braces: the primitive's deleted wrapper
                            claimed SubContent rendered inside Content's portal,
                            which would make this redundant — but a nested
                            `.tw-root` is inert, and being wrong the other way
                            costs the submenu its preflight. */}
                        <DropdownMenuSubContent className="tw-root">
                          {postingGroups.map((group) => (
                            <DropdownMenuItem
                              key={group.id}
                              onSelect={
                                onSelectGroup
                                  ? () => onSelectGroup(group)
                                  : undefined
                              }
                            >
                              <span className="tw:min-w-0 tw:flex-1 tw:truncate">
                                {group.name}
                              </span>
                              <span className="tw:shrink-0 tw:text-muted-foreground">
                                {group.accountIds.length}
                              </span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <Separator />
        </div>
      ) : null}

      {/* role="status": in prod this count changes as tiles are tapped, and it
          is the only feedback that the tap registered. */}
      <p role="status" className="tw:text-sm tw:text-muted-foreground">
        Post to <span className="tw:font-semibold tw:text-foreground">{selected}</span>{" "}
        socials
      </p>

      {visible.length === 0 ? (
        /* Reachable only by narrowing — an account list that is empty to begin
           with returns DestinationsEmpty above. Without this the grid would show
           just the Connect New tile, which reads as "you have no destinations"
           rather than "nothing matched". */
        <p className="tw:py-6 tw:text-center tw:text-sm tw:text-muted-foreground">
          No outputs match {trimmed ? <>“{query.trim()}”</> : "this filter"}.
        </p>
      ) : (
        <div className="tw:grid tw:grid-cols-3 tw:items-start tw:gap-x-5 tw:gap-y-6 tw:sm:grid-cols-4 tw:md:grid-cols-6">
          {/* Hidden while narrowing: "Connect New" is not a search result, and
              leaving it in made an empty result set look like one match. */}
          {narrowed ? null : <ConnectNewTile onSelect={onConnectNew} />}
          {visible.map((account) => (
            <OutputTile
              key={account.id}
              account={account}
              onSelect={onSelectAccount}
            />
          ))}
        </div>
      )}
    </div>
  )
}
